import { useCallback, useState } from "react";

import { generateMarketingSetupLink } from "@/api/orval/ageless-platform-features/ageless-platform-features";

import { buildAgelessMarketingSetupLink } from "./agelessMarketingPublicAccess";

/**
 * Lifetime of a shared Ageless Marketing setup link: 90 days, matching the ceiling the backend
 * token manager enforces. Long enough for a practice's web developer to schedule the work,
 * short enough that a forwarded link stops working.
 */
export const AGELESS_MARKETING_SETUP_LINK_EXPIRY_MINUTES = 129_600;

export type AgelessMarketingSetupLinkMinter = {
  /**
   * Mints (once per mounted consumer) the login-free setup link for this platform. Throws on
   * failure so callers decide what that means — copying must surface the error, whereas
   * attaching the link to an email should carry on without it.
   */
  readonly ensureSetupLink: () => Promise<string>;
  readonly isMinting: boolean;
  /** The link once minted, so callers can render it without re-requesting. */
  readonly setupLinkUrl: string | null;
};

/**
 * Mints the shareable Ageless Marketing setup link. Shared by the embed wizard's "send to my
 * web person" step and the marketing page's share callout so both hand out the same artifact.
 */
export function useAgelessMarketingSetupLink(platformId: string): AgelessMarketingSetupLinkMinter {
  const [setupLinkUrl, setSetupLinkUrl] = useState<string | null>(null);
  const [isMinting, setIsMinting] = useState(false);

  const ensureSetupLink = useCallback(async (): Promise<string> => {
    if (setupLinkUrl) {
      return setupLinkUrl;
    }

    setIsMinting(true);
    try {
      const response = await generateMarketingSetupLink(platformId, {
        expiryMinutes: AGELESS_MARKETING_SETUP_LINK_EXPIRY_MINUTES,
        requestedLanguageCode: "en_US",
      });

      const url = buildAgelessMarketingSetupLink(
        globalThis.location.origin,
        platformId,
        response.token,
      );
      setSetupLinkUrl(url);
      return url;
    } finally {
      setIsMinting(false);
    }
  }, [platformId, setupLinkUrl]);

  return { ensureSetupLink, isMinting, setupLinkUrl };
}
